/** Basics → Chat & app: what "Use operator default settings" does to this tour, read-only
 *  (the switch itself sits beside the tag-categories table — plan S1/S2).
 *
 *  While the tour uses the operator defaults (the stored value in view mode, the draft's in
 *  edit mode):
 *  - the operator has default settings: the guest app uses ITS location sharing and group
 *    chat, not the tour's own values below — the note names the effective values;
 *  - the operator has default tag categories only: the tour's own chat values apply;
 *  - the operator has neither: the dashboard's "Default Settings Not Set Up" — an amber
 *    note (icon and words), the tour inherits nothing.
 *  Each links to the switch, and to where the operator sets what the note names: its
 *  Defaults tab — the trip settings, the default tag categories, or the whole tab when
 *  nothing is set up. Nothing renders while the tour does not use the defaults. */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { useDraftField } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import {
  operatorDefaultsHref,
  type OperatorDefaultsSection,
} from '@/features/tour-operators/tenantLinks';

import { LOCATION_SHARING_CHOICES, TOUR_ENTITY } from '../catalog';
import { useTourRecord } from '../TourRecordContext';

export function OperatorDefaultsNote() {
  const { detail } = useTourRecord();
  const { mode, focusField } = useRecordEditor();
  const field = useDraftField(TOUR_ENTITY, detail.id, 'use_operator_defaults');
  const on = (mode === 'edit' ? field.value : field.baseline) === true;
  if (!on) return null;

  const defaults = detail.operator_defaults;
  const hasCategories = (detail.tag_categories_default ?? []).length > 0;
  const toSwitch = (
    <Button
      size="small"
      onClick={() => focusField(`${TOUR_ENTITY}:${detail.id}`, 'use_operator_defaults')}
      sx={{ py: 0, minHeight: 24, alignSelf: 'flex-start' }}
    >
      Go to the switch
    </Button>
  );
  const topId = detail.tenant?.id;
  /** The switch, then the operator's Defaults tab (none for a tour without an operator). */
  const links = (section: OperatorDefaultsSection | undefined, label: string) => (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignSelf: 'flex-start' }}>
      {toSwitch}
      {topId != null && (
        <Button
          size="small"
          component={RouterLink}
          to={operatorDefaultsHref(topId, section)}
          sx={{ py: 0, minHeight: 24 }}
        >
          {label}
        </Button>
      )}
    </Box>
  );

  if (!defaults && !hasCategories) {
    return (
      <Box data-operator-defaults="missing" sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <WarningAmberIcon aria-hidden sx={{ fontSize: 16, color: 'warning.main', mt: '2px' }} />
        <Typography variant="body2" sx={{ flex: '1 1 260px', minWidth: 0 }}>
          <b>Operator defaults missing.</b> This tour uses the operator’s default settings, but the operator has
          none set up — no default settings and no default tag categories — so it inherits nothing.
        </Typography>
        {links(undefined, 'Operator’s defaults')}
      </Box>
    );
  }

  const parts: string[] = [];
  if (defaults?.location_sharing !== null && defaults?.location_sharing !== undefined) {
    const label = LOCATION_SHARING_CHOICES.find((c) => c.value === defaults.location_sharing)?.label ?? `#${defaults.location_sharing}`;
    parts.push(`location sharing ${label.toLowerCase()}`);
  }
  if (defaults?.has_group_chat !== null && defaults?.has_group_chat !== undefined) {
    parts.push(`group chat ${defaults.has_group_chat ? 'on' : 'off'}`);
  }
  return (
    <Box data-operator-defaults="inherited" sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <InfoOutlinedIcon aria-hidden sx={{ fontSize: 16, color: 'info.main', mt: '2px' }} />
      <Typography variant="body2" sx={{ flex: '1 1 260px', minWidth: 0 }}>
        {parts.length > 0 ? (
          <>
            Uses the operator’s default settings: in the guest app, <b>{parts.join(' · ')}</b> — whatever this
            tour’s own values below say.
          </>
        ) : (
          <>Uses the operator’s default tag categories; the operator sets no default chat or location values, so this tour’s own apply.</>
        )}
      </Typography>
      {parts.length > 0
        ? links('trip-settings', 'Operator’s trip settings')
        : links('tag-categories', 'Operator’s default tag categories')}
    </Box>
  );
}
